import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { format } from "date-fns";
import { EducationLevel } from "../../../../__generated__/graphql";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  VStack,
  Box,
  Heading,
  Text,
  Flex,
  Divider,
  Image,
  Link,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import Loading from "_components/Loading";
import RequestFailed from "_components/RequestFailed";
import Navbar from "_components/Navbar/Navbar";
import Footer from "_components/Footer";
import { Helmet } from "react-helmet";
import { GET_SHAREABLE_APPLICATION } from "features/jobs/queries";
import { ExpandableText } from "features/jobs/expandable-text";

const formatEducationLevel = (level: EducationLevel): string => {
  switch (level) {
    case EducationLevel.Jssce:
      return "JSSCE";
    case EducationLevel.Ssce:
      return "SSCE";
    case EducationLevel.Nabteb:
      return "NABTEB";
    case EducationLevel.Kcse:
      return "KCSE";
    case EducationLevel.Ond:
      return "OND";
    case EducationLevel.Hnd:
      return "HND";
    case EducationLevel.Bachelors:
      return "Bachelor's Degree";
    case EducationLevel.Masters:
      return "Master's Degree";
    case EducationLevel.Doctorate:
      return "Doctorate";
    default:
      return level;
  }
};

export default function ShareableApplicationPage() {
  const { id } = useParams<{ id: string }>();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, loading, error } = useQuery(GET_SHAREABLE_APPLICATION, {
    variables: { id: id! },
    fetchPolicy: "cache-and-network",
  });

  const openImageModal = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    onOpen();
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    onClose();
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <VStack spacing={0} minH="100vh">
          <Box
            flex="1"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Loading />
          </Box>
        </VStack>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <VStack spacing={0} minH="100vh">
          <Box
            flex="1"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <RequestFailed
              text={`Error loading application: ${error.message}`}
            />
          </Box>
        </VStack>
        <Footer />
      </>
    );
  }

  const application = data?.getShareableApplication;

  if (!application) {
    return (
      <>
        <Navbar />
        <VStack spacing={0} minH="100vh">
          <Box
            flex="1"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="lg">Application not found</Text>
          </Box>
        </VStack>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Job Application - {application.applicant.firstName}</title>
      </Helmet>

      <Navbar />

      <Modal isOpen={isOpen} onClose={closeImageModal} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Media</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedImage && (
              <Flex justify="center">
                <Image
                  src={selectedImage}
                  alt="Full size media"
                  maxW="100%"
                  maxH="70vh"
                  objectFit="contain"
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <VStack spacing={0} minH="100vh">
        <Box maxW="4xl" mx="auto" py={8} px={4} flex="1" w="100%">
          {/* User Information */}
          <Box bg="white" border="1px" borderColor="gray.200" p={6} mb={8}>
            <Heading as="h3" size="lg" color="gray.900" mb={4}>
              Applicant information
            </Heading>

            <VStack align="start" spacing={4}>
              <VStack align="start" spacing={1}>
                <Text fontWeight="medium" fontFamily="AeonikPro" fontSize="lg">
                  Contact information
                </Text>
                <Text fontSize="md" color="gray.600">
                  {application.applicant.firstName}{" "}
                  {application.applicant.lastName}
                </Text>
                <Text fontSize="md" color="gray.600">
                  {application.applicant.email}
                </Text>
                <Text fontSize="md" color="gray.600">
                  {application.applicant.phoneNumber}
                </Text>
              </VStack>

              <VStack align="start" spacing={1}>
                <Text fontWeight="medium" fontFamily="AeonikPro" fontSize="lg">
                  Professional information
                </Text>
                <Text fontSize="md" color="gray.600">
                  {application.applicant.jobTitle}
                  {application.applicant.yearsOfExperience &&
                    ` • ${application.applicant.yearsOfExperience} years of experience`}
                </Text>
              </VStack>
            </VStack>
          </Box>

          {/* Application Details */}
          {(application.education.length > 0 ||
            application.employmentHistory.length > 0 ||
            application.projectHistory.length > 0 ||
            application.certifications.length > 0) && (
            <Box bg="white" border="1px" borderColor="gray.200" p={8}>
              <VStack spacing={8} align="stretch">
                {/* Education Section */}
                {application.education.length > 0 && (
                  <Box>
                    <Heading
                      as="h3"
                      size="lg"
                      color="gray.900"
                      borderBottom="1px"
                      borderColor="gray.200"
                      pb={2}
                      mb={4}
                    >
                      Education
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      {application.education.map((edu, index) => (
                        <Box key={edu.id || index}>
                          <Flex justify="space-between" align="start">
                            <Box flex="1">
                              <Text
                                fontWeight="medium"
                                fontSize="lg"
                                color="gray.900"
                              >
                                {edu.schoolName}
                              </Text>
                              <Text fontSize="md" color="gray.600">
                                {formatEducationLevel(edu.qualification)}
                                {edu.fieldOfStudy && ` in ${edu.fieldOfStudy}`}
                              </Text>
                            </Box>
                          </Flex>
                          {index < application.education.length - 1 && (
                            <Divider mt={4} borderColor="gray.100" />
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Employment History Section */}
                {application.employmentHistory.length > 0 && (
                  <Box>
                    <Heading
                      as="h3"
                      size="lg"
                      color="gray.900"
                      borderBottom="1px"
                      borderColor="gray.200"
                      pb={2}
                      mb={4}
                    >
                      Employment history
                    </Heading>
                    <VStack spacing={6} align="stretch">
                      {application.employmentHistory.map((emp, index) => (
                        <Box key={emp.id}>
                          <Flex
                            direction={{ base: "column", md: "row" }}
                            justify="space-between"
                            align={{ base: "start", md: "start" }}
                          >
                            <Box flex="1">
                              <Text
                                fontWeight="medium"
                                fontSize="lg"
                                color="gray.900"
                              >
                                {emp.jobTitle}
                              </Text>
                              <Text fontSize="md" color="gray.600">
                                {emp.companyName}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: "left", md: "right" }}>
                              <Text fontSize="md" color="gray.500">
                                {format(new Date(emp.startedAt), "MMM yyyy")} -{" "}
                                {emp.endedAt
                                  ? format(new Date(emp.endedAt), "MMM yyyy")
                                  : "Present"}
                              </Text>
                            </Box>
                          </Flex>
                          {emp.description && (
                            <ExpandableText text={emp.description} />
                          )}
                          {emp.media && emp.media.length > 0 && (
                            <Box mt={3}>
                              <Text fontSize="sm" color="gray.500" mb={2}>
                                Media:
                              </Text>
                              <Flex flexWrap="wrap" gap={2}>
                                {emp.media.map((mediaItem, mediaIndex) => (
                                  <Box
                                    key={mediaIndex}
                                    as="button"
                                    onClick={() =>
                                      openImageModal(mediaItem.originalUrl)
                                    }
                                    position="relative"
                                    cursor="pointer"
                                    _hover={{ opacity: 0.8 }}
                                  >
                                    <Image
                                      src={mediaItem.thumbnailUrl}
                                      alt={`Employment media ${mediaIndex + 1}`}
                                      w="16"
                                      h="16"
                                      objectFit="cover"
                                      border="1px"
                                      borderColor="gray.200"
                                      _hover={{ borderColor: "gray.400" }}
                                      transition="border-color 0.2s"
                                    />
                                  </Box>
                                ))}
                              </Flex>
                            </Box>
                          )}
                          {index < application.employmentHistory.length - 1 && (
                            <Divider mt={6} borderColor="gray.100" />
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Project History Section */}
                {application.projectHistory.length > 0 && (
                  <Box>
                    <Heading
                      as="h3"
                      size="lg"
                      color="gray.900"
                      borderBottom="1px"
                      borderColor="gray.200"
                      pb={2}
                      mb={4}
                    >
                      Project history
                    </Heading>
                    <VStack spacing={6} align="stretch">
                      {application.projectHistory.map((project, index) => (
                        <Box key={project.id || index}>
                          <Flex
                            direction={{ base: "column", md: "row" }}
                            justify="space-between"
                            align={{ base: "start", md: "start" }}
                          >
                            <Box flex="1">
                              <Text
                                fontWeight="medium"
                                fontSize="lg"
                                color="gray.900"
                              >
                                {project.name}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: "left", md: "right" }}>
                              <Text fontSize="md" color="gray.500">
                                {project.startedAt &&
                                  format(
                                    new Date(project.startedAt),
                                    "MMM yyyy"
                                  )}
                                {project.startedAt && project.endedAt && " - "}
                                {project.endedAt &&
                                  format(new Date(project.endedAt), "MMM yyyy")}
                              </Text>
                            </Box>
                          </Flex>
                          {project.description && (
                            <ExpandableText text={project.description} />
                          )}
                          {project.media && project.media.length > 0 && (
                            <Box mt={3}>
                              <Text fontSize="sm" color="gray.500" mb={2}>
                                Media:
                              </Text>
                              <Flex flexWrap="wrap" gap={2}>
                                {project.media.map((mediaItem, mediaIndex) => (
                                  <Box
                                    key={mediaIndex}
                                    as="button"
                                    onClick={() =>
                                      openImageModal(mediaItem.originalUrl)
                                    }
                                    position="relative"
                                    cursor="pointer"
                                    _hover={{ opacity: 0.8 }}
                                  >
                                    <Image
                                      src={mediaItem.thumbnailUrl}
                                      alt={`Project media ${mediaIndex + 1}`}
                                      w="16"
                                      h="16"
                                      objectFit="cover"
                                      border="1px"
                                      borderColor="gray.200"
                                      _hover={{ borderColor: "gray.400" }}
                                      transition="border-color 0.2s"
                                    />
                                  </Box>
                                ))}
                              </Flex>
                            </Box>
                          )}
                          {index < application.projectHistory.length - 1 && (
                            <Divider mt={6} borderColor="gray.100" />
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Certifications Section */}
                {application.certifications.length > 0 && (
                  <Box>
                    <Heading
                      as="h3"
                      size="lg"
                      color="gray.900"
                      borderBottom="1px"
                      borderColor="gray.200"
                      pb={2}
                      mb={4}
                    >
                      Certifications
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      {application.certifications.map((cert, index) => (
                        <Box key={cert.id || index}>
                          <Flex
                            direction={{ base: "column", md: "row" }}
                            justify="space-between"
                            align={{ base: "start", md: "start" }}
                          >
                            <Box flex="1">
                              <Text
                                fontWeight="medium"
                                fontSize="lg"
                                color="gray.900"
                              >
                                {cert.name}
                              </Text>
                              <Text fontSize="md" color="gray.600">
                                {cert.issuedBy}
                              </Text>
                            </Box>
                            <Box textAlign={{ base: "left", md: "right" }}>
                              <Text fontSize="md" color="gray.500">
                                {format(new Date(cert.issuedAt), "MMM yyyy")}
                              </Text>
                            </Box>
                          </Flex>
                          <Flex gap={2} align="center" mt={2}>
                            {cert.issuerCertificationId && (
                              <Text fontSize="md" color="gray.500">
                                Certification ID: {cert.issuerCertificationId}
                              </Text>
                            )}
                            {cert.url && (
                              <Button
                                as={Link}
                                href={cert.url}
                                isExternal
                                variant="link"
                                size="sm"
                                fontSize="sm"
                                rightIcon={<ExternalLinkIcon />}
                              >
                                View certificate
                              </Button>
                            )}
                          </Flex>
                          {index < application.certifications.length - 1 && (
                            <Divider mt={4} borderColor="gray.100" />
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>
          )}

          {/* Footer */}
          <Box mt={8} textAlign="center">
            <Text fontSize="md" color="gray.500">
              Application submitted on{" "}
              {format(
                new Date(application.createdAt),
                "MMMM d, yyyy 'at' h:mm a"
              )}
            </Text>
          </Box>
        </Box>
      </VStack>

      <Footer />
    </>
  );
}
